import React, { useState, useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import Sidebar from "./Sidebar";
import Header from "./Header";
import MobileNav from "./MobileNav";
import GlobalSearch from "../GlobalSearch";
import SubscriptionBanner from "../SubscriptionBanner";
import CustomerViewBanner from "../CustomerViewBanner";
import OnboardingWizard from "../OnboardingWizard";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Layout = () => {
  const { isAuthenticated, loading, user, isPlatformAdminMode } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Phase F.5 Batch 3 — auto-trigger Onboarding Wizard on first login.
  // Admin-only; safe to silently skip on failures (e.g. portal users).
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (user.account_type && user.account_type !== "internal") return;
    if (!(user.role === "office_manager" || user.role === "field_supervisor")) return;
    axios.get(`${API}/onboarding/state`).then((r) => {
      if (r.data && !r.data.completed && !r.data.skipped) setShowOnboarding(true);
    }).catch(() => {});
  }, [isAuthenticated, user]);

  // iter147 — Allow any page to reopen the guided tour by dispatching a
  // window event. Used by ProfileSettings "Restart Guided Tour" button.
  useEffect(() => {
    const openTour = () => setShowOnboarding(true);
    window.addEventListener("bg:open-onboarding-tour", openTour);
    return () => window.removeEventListener("bg:open-onboarding-tour", openTour);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Phase 19.13: Portal users must NEVER reach the internal (owner) shell.
  // Redirect them to their portal landing. The /api also enforces with 403,
  // but the UI was rendering broken pages with red error toasts.
  // Exception: /portal/* routes (Phase D — Comms Hub) are mounted inside this
  // shell intentionally so portal users can submit requests via the standard layout.
  const isPortalRoute = location.pathname.startsWith("/portal/");
  if (user?.account_type === "client_portal" && !isPortalRoute) {
    return <Navigate to="/client-portal" replace state={{ from: location }} />;
  }
  if (user?.account_type === "employee_portal" && !isPortalRoute) {
    return <Navigate to="/employee-portal" replace state={{ from: location }} />;
  }

  // Phase 19.13: /admin/* routes are reserved for platform admins.
  // Non-admin internal users hitting them should be sent to /dashboard.
  // Exception: /admin/communications is open to office_managers within their own tenant.
  if (
    location.pathname.startsWith("/admin") &&
    location.pathname !== "/admin/communications" &&
    !user?.is_platform_admin
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Customer View Mode Banner (only when admin has flipped "View as Customer") */}
      <CustomerViewBanner />

      {/* Subscription Status Banner */}
      <SubscriptionBanner />

      {/* Global Search */}
      <GlobalSearch open={searchOpen} setOpen={setSearchOpen} />

      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 transform bg-card border-r transition-transform duration-200 ease-in-out md:hidden ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <Sidebar onClose={() => setMobileMenuOpen(false)} />
      </aside>

      {/* Main Content */}
      <div className="md:pl-64 min-h-screen transition-all duration-300 pb-32 md:pb-0">
        <Header
          onMenuClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          menuOpen={mobileMenuOpen}
          onSearchClick={() => setSearchOpen(true)}
        />
        <main className="p-4 md:p-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Navigation — hidden when in platform-admin (Owner Console) view;
          re-appears automatically when admin flips "View as Customer" */}
      {!isPlatformAdminMode && (
        <MobileNav 
          onSearchClick={() => setSearchOpen(true)}
          onMenuClick={() => setMobileMenuOpen(true)}
        />
      )}

      {/* Phase F.5 Batch 3 — Onboarding Wizard (auto-opens on first login for admins) */}
      <OnboardingWizard open={showOnboarding} onClose={() => setShowOnboarding(false)} />
    </div>
  );
};

export default Layout;
